import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { ReactionSummary } from '../services/api/reactions';
import { useAuth } from './AuthContext';

interface ReactionCacheContextType {
  get: (key: string) => ReactionSummary | undefined;
  set: (key: string, summary: ReactionSummary) => void;
  setBatch: (entries: Record<string, ReactionSummary>, keyPrefix: string) => void;
  invalidate: (key: string) => void;
  /** Drops every entry. Called on sign-out to avoid leaking another user's reactions. */
  clear: () => void;
}

const ReactionCacheContext = createContext<ReactionCacheContextType | null>(null);

export function albumCacheKey(albumId: string): string {
  return `album:${albumId}`;
}

export function photoCacheKey(albumId: string, photoId: string): string {
  return `photo:${albumId}:${photoId}`;
}

/**
 * Per-entry TTL (ms). Mirrors the IndexedDB TTL for `reactions:*` keys
 * (10 minutes) — past this, an in-memory entry is treated as missing on read,
 * forcing the caller to re-fetch.
 */
const ENTRY_TTL_MS = 10 * 60 * 1000;

/**
 * Soft cap on number of cached entries. When exceeded, oldest entries are
 * evicted on insert. Prevents unbounded memory growth as users browse many
 * albums/photos in a single session.
 */
const MAX_ENTRIES = 500;

interface CachedEntry {
  summary: ReactionSummary;
  storedAt: number;
}

export const ReactionCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const cacheRef = useRef<Map<string, CachedEntry>>(new Map());
  const { isAuthenticated } = useAuth();

  // Bumps an existing key to most-recently-used position in the LRU ordering.
  const touch = useCallback((key: string) => {
    const entry = cacheRef.current.get(key);
    if (entry !== undefined) {
      cacheRef.current.delete(key);
      cacheRef.current.set(key, entry);
    }
  }, []);

  const evictIfNeeded = useCallback(() => {
    while (cacheRef.current.size > MAX_ENTRIES) {
      const oldestKey = cacheRef.current.keys().next().value;
      if (oldestKey === undefined) break;
      cacheRef.current.delete(oldestKey);
    }
  }, []);

  const get = useCallback((key: string) => {
    const entry = cacheRef.current.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.storedAt > ENTRY_TTL_MS) {
      cacheRef.current.delete(key);
      return undefined;
    }
    touch(key);
    return entry.summary;
  }, [touch]);

  const set = useCallback((key: string, summary: ReactionSummary) => {
    cacheRef.current.delete(key); // ensure re-insert at end for LRU
    cacheRef.current.set(key, { summary, storedAt: Date.now() });
    evictIfNeeded();
  }, [evictIfNeeded]);

  const setBatch = useCallback((entries: Record<string, ReactionSummary>, keyPrefix: string) => {
    const now = Date.now();
    for (const [id, summary] of Object.entries(entries)) {
      const key = `${keyPrefix}${id}`;
      cacheRef.current.delete(key);
      cacheRef.current.set(key, { summary, storedAt: now });
    }
    evictIfNeeded();
  }, [evictIfNeeded]);

  const invalidate = useCallback((key: string) => {
    cacheRef.current.delete(key);
  }, []);

  const clear = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  // Drop all cached reactions on sign-out so a subsequent user on the same
  // browser cannot see the previous user's reaction state. Only clears on a
  // true authenticated → unauthenticated transition (not on initial mount,
  // which would otherwise wipe entries set during render in tests / SSR).
  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      cacheRef.current.clear();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  return (
    <ReactionCacheContext.Provider value={{ get, set, setBatch, invalidate, clear }}>
      {children}
    </ReactionCacheContext.Provider>
  );
};

export function useReactionCache(): ReactionCacheContextType {
  const ctx = useContext(ReactionCacheContext);
  if (!ctx) throw new Error('useReactionCache must be used within ReactionCacheProvider');
  return ctx;
}
